import React, { useState, useEffect, useCallback } from 'react';
import { getRandomWordAndImage } from '../services/api';
import { WORDS } from '../constants/wordList';
import Grid from './Grid';
import Keyboard from './Keyboard';
import Confetti from 'react-confetti';
import { initDictionary, isValidWord } from '../utils/dictionary';

const INITIAL_ZOOM = 5000;
const ZOOM_DECREASE = 1000;
const MIN_ZOOM = 100;
const WIN_MODAL_DELAY = 1200;
const ERROR_DISPLAY_TIME = 2000;

const COMMON_WORDS = new Set(WORDS);
const validWordCache = new Map();
const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Load cache from localStorage on startup
try {
  const savedCache = localStorage.getItem('wordValidationCache');
  if (savedCache) {
    const { cache, timestamp } = JSON.parse(savedCache);
    if (Date.now() - timestamp < CACHE_EXPIRY) {
      Object.entries(cache).forEach(([word, isValid]) => {
        validWordCache.set(word, isValid);
      });
    }
  }
} catch (error) {
  console.error('Error loading cache:', error);
}

// Batch API caller with rate limiting
const batchCheckWords = async (words) => {
  const results = new Map();
  const chunk = 1; // Number of simultaneous requests
  const delay = 100; // Delay between chunks in ms

  for (let i = 0; i < words.length; i += chunk) {
    const batch = words.slice(i, i + chunk);
    const promises = batch.map(word => 
      fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${word.toLowerCase()}`)
        .then(response => ({ word, isValid: response.ok }))
        .catch(() => ({ word, isValid: false }))
    );

    const batchResults = await Promise.all(promises);
    batchResults.forEach(({ word, isValid }) => {
      results.set(word, isValid);
      validWordCache.set(word, isValid);
    });

    if (i + chunk < words.length) {
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  // Save updated cache to localStorage
  try {
    const cacheObject = Object.fromEntries(validWordCache);
    localStorage.setItem('wordValidationCache', JSON.stringify({
      cache: cacheObject,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error('Error saving cache:', error);
  }

  return results;
};

const Game = ({ isHardcore = false }) => {
  const getRandomWord = useCallback(async () => {
    const { word, imageUrl } = await getRandomWordAndImage();
    return { word, imageUrl };
  }, []);

  const [gameState, setGameState] = useState({
    solution: '',
    imageUrl: '',
    guesses: Array(5).fill(''),
    currentGuess: '',
    currentRow: 0,
    gameOver: false,
    zoomLevel: INITIAL_ZOOM,
    isTransitioning: true,
    isChecking: false,
    loading: true
  });

  // Store only minimal modal data (not rendered elements)
  const [modalState, setModalState] = useState({
    showModal: false,
    title: '',
    isWin: false,
  });

  const [errorState, setErrorState] = useState({
    showError: false,
    errorMessage: ''
  });

  const [confettiState, setConfettiState] = useState({
    showConfetti: false,
    confettiOpacity: 1
  });

  // This state now holds status strings like "A-correct", "B-present", etc.
  const [usedLetters, setUsedLetters] = useState(new Set());

  const [timeLeft, setTimeLeft] = useState(60);
  const [timerActive, setTimerActive] = useState(isHardcore);

  const [showShareToast, setShowShareToast] = useState(false);

  const [copyButtonText, setCopyButtonText] = useState('Copy Results');

  // Initial word and image fetch
  useEffect(() => {
    const initializeGame = async () => {
      const { word, imageUrl } = await getRandomWordAndImage();
      setGameState(prev => ({
        ...prev,
        solution: word,
        imageUrl: imageUrl,
        loading: false
      }));
    };

    initializeGame();
  }, []);

  // Pre-fetch validation for common letter combinations
  useEffect(() => {
    const preValidateCommonPatterns = async () => {
      const commonPatterns = new Set();
      
      // Get all 5-letter combinations from previous guesses
      gameState.guesses.forEach(guess => {
        if (guess) {
          for (let i = 0; i < guess.length - 1; i++) {
            commonPatterns.add(guess.slice(i, i + 2));
          }
        }
      });

      // Filter out already cached patterns
      const patternsToCheck = Array.from(commonPatterns)
        .filter(pattern => !validWordCache.has(pattern));

      if (patternsToCheck.length > 0) {
        await batchCheckWords(patternsToCheck);
      }
    };

    preValidateCommonPatterns();
  }, [gameState.guesses]);

  useEffect(() => {
    // Initialize dictionary when component mounts
    initDictionary();
  }, []);

  const checkWord = async (word) => {
    // Replace API call with local dictionary check
    return isValidWord(word);
  };

  const showErrorMessage = useCallback((message) => {
    setErrorState({
      showError: true,
      errorMessage: message
    });

    setTimeout(() => {
      setErrorState(prev => ({
        ...prev,
        showError: false
      }));
    }, ERROR_DISPLAY_TIME);
  }, []);

  const startConfettiAnimation = useCallback(() => {
    setConfettiState({
      showConfetti: true,
      confettiOpacity: 1
    });
    
    setTimeout(() => {
      setConfettiState(prev => ({
        ...prev,
        confettiOpacity: 0
      }));
    }, 2000);

    setTimeout(() => {
      setConfettiState(prev => ({
        ...prev,
        showConfetti: false
      }));
    }, 4000);
  }, []);

  // Instead of storing rendered modal content, store just minimal info.
  const displayModal = useCallback((title, isWin) => {
    setModalState({
      showModal: true,
      title,
      isWin,
    });
  }, []);

  const resetGame = useCallback(async () => {
    setGameState(prev => ({
      ...prev,
      loading: true
    }));

    const { word, imageUrl } = await getRandomWordAndImage();
    
    setGameState({
      solution: word,
      imageUrl: imageUrl,
      guesses: Array(5).fill(''),
      currentGuess: '',
      currentRow: 0,
      gameOver: false,
      zoomLevel: INITIAL_ZOOM,
      isTransitioning: false,
      isChecking: false,
      loading: false
    });
    setModalState({
      showModal: false,
      title: '',
      isWin: false,
    });
    setConfettiState({
      showConfetti: false,
      confettiOpacity: 1
    });
    setUsedLetters(new Set());
    if (isHardcore) {
      setTimeLeft(60);
      setTimerActive(true);
    }

    setTimeout(() => {
      setGameState(prev => ({
        ...prev,
        isTransitioning: true
      }));
    }, 50);
  }, [isHardcore]);

  // Updated updateUsedLetters: now it computes statuses (correct, present, absent)
  const updateUsedLetters = useCallback((guess) => {
    const newUsedLetters = new Set(usedLetters);
    const currentGuess = guess.toUpperCase();
    const solution = gameState.solution.toUpperCase();

    // Create a map for counting letters in the solution
    const remainingLetters = {};
    [...solution].forEach(letter => {
      remainingLetters[letter] = (remainingLetters[letter] || 0) + 1;
    });

    // First pass: mark correct (green) letters
    [...currentGuess].forEach((letter, i) => {
      if (solution[i] === letter) {
        newUsedLetters.add(`${letter}-correct`);
        remainingLetters[letter]--;
      }
    });

    // Second pass: mark present (yellow) or absent (gray) letters
    [...currentGuess].forEach((letter, i) => {
      if (solution[i] !== letter) {
        if (remainingLetters[letter] > 0) {
          newUsedLetters.add(`${letter}-present`);
          remainingLetters[letter]--;
        } else {
          newUsedLetters.add(`${letter}-absent`);
        }
      }
    });

    setUsedLetters(newUsedLetters);
  }, [gameState.solution, usedLetters]);

  const onKeyPress = useCallback(async (key) => {
    if (gameState.gameOver) return;

    if (key === 'ENTER') {
      if (gameState.currentGuess.length !== 5) {
        showErrorMessage('Word must be 5 letters');
        return;
      }

      setGameState(prev => ({
        ...prev,
        isChecking: true
      }));

      const isValid = await checkWord(gameState.currentGuess);
      
      if (!isValid) {
        showErrorMessage('Not a valid word');
        setGameState(prev => ({
          ...prev,
          isChecking: false
        }));
        return;
      }

      const newGuesses = [...gameState.guesses];
      newGuesses[gameState.currentRow] = gameState.currentGuess;

      if (gameState.currentGuess === gameState.solution) {
        setGameState(prev => ({
          ...prev,
          guesses: newGuesses,
          gameOver: true,
          zoomLevel: MIN_ZOOM,
          isChecking: false
        }));
        startConfettiAnimation();
        setTimeout(() => {
          displayModal('Congratulations! 🎉', true);
        }, WIN_MODAL_DELAY);
      } else {
        const newZoom = Math.max(MIN_ZOOM, gameState.zoomLevel - ZOOM_DECREASE);
        
        if (gameState.currentRow === 4) {
          setGameState(prev => ({
            ...prev,
            guesses: newGuesses,
            gameOver: true,
            zoomLevel: MIN_ZOOM,
            isChecking: false
          }));
          displayModal('Sorry, try again!', false);
        } else {
          setGameState(prev => ({
            ...prev,
            guesses: newGuesses,
            zoomLevel: newZoom,
            currentRow: prev.currentRow + 1,
            currentGuess: '',
            isChecking: false
          }));
        }
      }

      // Update the usedLetters with statuses for the submitted guess
      updateUsedLetters(gameState.currentGuess);
    } else if (key === '⌫') {
      setGameState(prev => ({
        ...prev,
        currentGuess: prev.currentGuess.slice(0, -1)
      }));
    } else if (gameState.currentGuess.length < 5 && /^[A-Z]$/.test(key)) {
      setGameState(prev => ({
        ...prev,
        currentGuess: prev.currentGuess + key
      }));
    }
  }, [gameState, checkWord, showErrorMessage, startConfettiAnimation, displayModal, updateUsedLetters]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (gameState.gameOver) return;
      
      if (e.key === 'Enter' || e.key === 'Backspace') {
        e.preventDefault();
      }
      
      if (e.key === 'Enter') {
        onKeyPress('ENTER');
      } else if (e.key === 'Backspace') {
        onKeyPress('⌫');
      } else if (/^[A-Za-z]$/.test(e.key)) {
        onKeyPress(e.key.toUpperCase());
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameState.gameOver, onKeyPress]);

  useEffect(() => {
    let timer;
    if (isHardcore && timerActive && timeLeft > 0 && !gameState.gameOver) {
      timer = setInterval(() => {
        setTimeLeft(time => {
          if (time <= 1) {
            setGameState(prev => ({
              ...prev,
              gameOver: true,
              zoomLevel: MIN_ZOOM
            }));
            displayModal("Time's Up!", false);
          }
          return time - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isHardcore, timerActive, timeLeft, gameState.gameOver, gameState.solution, displayModal]);

  const renderTimer = () => {
    if (!isHardcore) return null;
    return (
      <div className="timer">
        Time Left: {timeLeft}s
      </div>
    );
  };

  const generateShareText = () => {
    const attempts = gameState.currentRow + 1;
    const isWin = gameState.guesses[attempts - 1] === gameState.solution;
    
    let shareText = `Zoomed In ${isWin ? `${attempts}/5` : '😔'} 📦🔍\n\n`;
    
    for (let i = 0; i < attempts; i++) {
      const guess = gameState.guesses[i];
      if (!guess) continue;
      
      let rowText = '';
      for (let j = 0; j < guess.length; j++) {
        const letter = guess[j];
        if (letter === gameState.solution[j]) {
          rowText += '🟩';
        }
        else if (gameState.solution.includes(letter)) {
          rowText += '🟨';
        } else {
          rowText += '⬛';
        }
      }
      shareText += rowText + '\n';
    }
    
    shareText += '\nPlay at: https://zoomedin.com';
    return shareText;
  };

  const handleShare = async () => {
    const shareText = generateShareText();

    if (navigator.share && navigator.canShare) {
      try {
        await navigator.share({
          text: shareText
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareText);
        setCopyButtonText('Results copied!');
        setTimeout(() => setCopyButtonText('Copy Results'), 2000);
        setShowShareToast(true);
        setTimeout(() => setShowShareToast(false), 2000);
      } catch (error) {
        console.error('Error copying to clipboard:', error);
      }
    }
  };

  const handleCopyResults = async () => {
    const shareText = generateShareText();
    
    try {
      await navigator.clipboard.writeText(shareText);
      setCopyButtonText('Results Copied!');
      setTimeout(() => {
        setCopyButtonText('Copy Results');
      }, 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  const renderGameOverContent = (isWin) => {
    return (
      <div style={{ textAlign: 'center' }}>
        <p>{isWin ? 'Congratulations!' : 'Better luck next time!'}</p>
        <p style={{ 
          fontSize: '28px', 
          fontWeight: 'bold',
          color: '#ffffff',
          margin: '15px 0'
        }}>
          {gameState.solution}
        </p>
        <div className="modal-image-container" style={{
          width: '200px',
          height: '200px',
          margin: '20px auto',
          borderRadius: '10px',
          overflow: 'hidden'
        }}>
          <img 
            src={gameState.imageUrl}
            alt={gameState.solution}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        </div>
        <div className="modal-buttons">
          <button 
            className="modal-button copy-button"
            onClick={handleCopyResults}
          >
            {copyButtonText}
          </button>
          <button 
            className="modal-button share-button"
            onClick={handleShare}
          >
            Share
          </button>
          <button 
            className="modal-button"
            onClick={resetGame}
          >
            Play Again
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="game">
      {errorState.showError && (
        <div className="error-popup">
          {errorState.errorMessage}
        </div>
      )}
      {confettiState.showConfetti && (
        <div className="confetti-container" style={{ opacity: confettiState.confettiOpacity }}>
          <Confetti
            width={window.innerWidth}
            height={window.innerHeight}
            recycle={confettiState.showConfetti}
            numberOfPieces={300}
            gravity={0.2}
            wind={0.01}
            tweenDuration={4000}
            opacity={confettiState.confettiOpacity}
            colors={['#FFD700', '#FFA500', '#FF6347', '#FF69B4', '#4169E1', '#9B59B6']}
          />
        </div>
      )}
      {modalState.showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 className="modal-title">{modalState.title}</h2>
            {renderGameOverContent(modalState.isWin)}
          </div>
        </div>
      )}
      {isHardcore && renderTimer()}
      <div className="game-top-container">
        <div className="image-container">
          {gameState.loading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
              <span className="loading-text">Loading image...</span>
            </div>
          ) : (
            <img 
              src={gameState.imageUrl} 
              alt="Guess the word" 
              style={{ 
                width: `${gameState.zoomLevel}%`,
                height: `${gameState.zoomLevel}%`,
                objectFit: 'cover',
                transition: gameState.isTransitioning ? 'all 0.8s ease-in-out' : 'none'
              }}
            />
          )}
        </div>
        <Grid 
          guesses={gameState.guesses}
          currentGuess={gameState.currentGuess}
          currentRow={gameState.currentRow}
          solution={gameState.solution}
        />
      </div>
      <div className="game-bottom-container">
        <Keyboard 
          onKeyPress={onKeyPress}
          usedLetters={usedLetters}
        />
      </div>
      {showShareToast && (
        <div className="toast">
          Copied to clipboard!
        </div>
      )}
    </div>
  );
};

export default Game;
